import ReactDOM from 'react-dom'
import React, { useEffect, type FC } from 'react'
import lottie from 'lottie-web'
import LoadingJson from './assets/loading4.5.json'

import './index.scss'

interface LoadingProps {
  show?: boolean;
  label?: string | null;
  success?: boolean;
}

interface ShowOptions {
  success?: boolean;
  autoClose?: boolean;
  delay?: number;
}

type CallBack = () => void
type ShowFunc = (
  label?: string | null,
  container?: HTMLElement | string | null,
  key?: string,
  options?: ShowOptions
) => CallBack
type HideFunc = (arg?: string | boolean) => void

interface LoadingFuncs {
  show: ShowFunc;
  success: ShowFunc;
  hide: HideFunc;
}

const Loading: FC<LoadingProps> & LoadingFuncs = (props: LoadingProps) => {
  const { show, label, success } = props
  const loadingRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!show || success) {
      return
    }

    lottie.loadAnimation({
      container: loadingRef.current as HTMLDivElement,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: LoadingJson
    })
  }, [show, success])

  if (!show) {
    return null
  }

  return (
    <div className='loadingContainer'>
      <div className='loadingImgContainer' style={label ? { backgroundColor: 'rgba(0, 0, 0, 0.8)' } : undefined}>
        {success ? (<div className='successIcon' />) : (<div className='loadingImg' ref={loadingRef} />)}
        {label && (<p className='loadingInfo'>{label}</p>)}
      </div>
    </div>
  )
}

const loadingShowArray: ({ key: string; callback: CallBack; })[] = []

const hide: HideFunc = (arg?: string | boolean) => {
  if (typeof arg === 'string') {
    const exist = loadingShowArray.find(s => s.key === arg)
    if (!exist) {
      throw new Error(`loading instance with key ${arg} cannot be found`)
    }
    exist.callback()
    return
  }

  if (arg) {
    // remove all
    loadingShowArray.forEach(item => (
      item.callback()
    ))
    return
  }

  // remove lastest one
  const lastest = loadingShowArray[loadingShowArray.length - 1]
  lastest?.callback()
}

const show: ShowFunc = (
  label?: string | null,
  container?: HTMLElement | string | null,
  key?: string,
  options?: ShowOptions
) => {
  let finalContainer: HTMLElement | undefined | null
  if (!container) {
    finalContainer = document.body
  } else if (typeof container === 'string') {
    finalContainer = document.getElementById(container)
  } else {
    finalContainer = container
  }

  if (!finalContainer && typeof container === 'string') {
    throw new Error(`element with id ${container} cannot be found`)
  }

  if (!finalContainer) {
    throw new Error('document.body cannot be found')
  }

  let finalKey: string
  if (key) {
    finalKey = key
  } else {
    const timesmap = new Date().getTime().toString()
    const isExist = loadingShowArray.findIndex(s => s.key === timesmap) >= 0
    finalKey = isExist ? `${timesmap}${Math.round(Math.random() * 99999999)}` : timesmap
  }

  const el = document.createElement('div')
  // el.setAttribute('class', 'loadingContainer');

  ReactDOM.render(<Loading
    key={finalKey}
    label={label}
    show
    success={options?.success}
  />, el)
  finalContainer.appendChild(el)

  const callback = () => {
    ReactDOM.unmountComponentAtNode(el)
    const index = loadingShowArray.findIndex(s => s.key === finalKey)
    index >= 0 && loadingShowArray.splice(index, 1)
  }
  loadingShowArray.push({ key: finalKey, callback })

  if (options?.autoClose) {
    setTimeout(() => {
      callback?.()
    }, options?.delay)
  }
  return callback
}

// success是新增的状态，表示的是加载成功之后的状态。
const success: ShowFunc = (
  label?: string | null,
  container?: HTMLElement | string | null,
  key?: string,
  options?: ShowOptions
) => show(label, container, key, {
  success: true,
  autoClose: true,
  delay: 1000,
  ...options
})

Loading.show = show
Loading.success = success
Loading.hide = hide

export default Loading
